import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** A_face = W · H_sash — fume hood elevation with sash, airfoil, baffle, exhaust stack. */
export function DiagramFumehoodFaceArea() {
  const hx = 80, hy = 40, hw = 190, hh = 175;
  const sashY = 110; // bottom of sash (opening top)

  return (
    <DiagramCanvas>
      {/* Exhaust stack stub */}
      <Rect x={hx + hw / 2 - 14} y={20} width={28} height={hy - 20} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.4" />
      {[0, 1, 2].map(i => (
        <Path key={i} d={`M${hx + hw / 2 - 8 + i * 8},18 q-2,-4 0,-8`} fill="none" stroke={DT.dim} strokeWidth="0.8" opacity={0.7} />
      ))}

      {/* Hood body */}
      <Rect x={hx} y={hy} width={hw} height={hh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      {/* Interior baffle (back wall rear slots) */}
      <Line x1={hx + 14} y1={hy + 10} x2={hx + hw - 14} y2={hy + 10} stroke={DT.stroke} strokeWidth="1.1" />
      {[0, 1, 2, 3].map(i => (
        <Line key={i} x1={hx + 30 + i * 38} y1={hy + 4} x2={hx + 30 + i * 38} y2={hy + 16} stroke={DT.stroke} strokeWidth="0.8" />
      ))}
      <SvgText x={hx + hw / 2} y={hy + 22} fill={DT.desc} fontSize="7" fontFamily={DT.mono} textAnchor="middle">rear baffle slots</SvgText>

      {/* Sash (glass, raised) */}
      <Rect x={hx} y={hy + 8} width={hw} height={sashY - hy - 8} fill="rgba(122,170,187,0.12)" stroke={DT.stroke} strokeWidth="0.8" />
      <Line x1={hx} y1={sashY} x2={hx + hw} y2={sashY} stroke={DT.stroke} strokeWidth="3.2" />
      {/* Sash handle */}
      <Rect x={hx + hw / 2 - 18} y={sashY - 6} width={36} height={5} fill={DT.stroke} />
      <SvgText x={hx + hw / 2} y={hy + 70} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">sash (glass)</SvgText>

      {/* Face opening (working aperture) */}
      <Rect x={hx} y={sashY} width={hw} height={hh - (sashY - hy) - 8} fill="rgba(255,107,26,0.14)" stroke={DT.varColor} strokeWidth="1.6" />
      {/* Airfoil sill */}
      <Path d={`M${hx},${hy + hh - 10} Q${hx + hw / 2},${hy + hh - 2} ${hx + hw},${hy + hh - 10}`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
      <SvgText x={hx + hw / 2} y={hy + hh - 2} fill={DT.desc} fontSize="7" fontFamily={DT.mono} textAnchor="middle">airfoil</SvgText>

      {/* Width dimension */}
      <Line x1={hx} y1={hy + hh + 14} x2={hx + hw} y2={hy + hh + 14} stroke={DT.varColor} strokeWidth="1.2" />
      <Polygon points={`${hx},${hy + hh + 14} ${hx + 7},${hy + hh + 10} ${hx + 7},${hy + hh + 18}`} fill={DT.varColor} />
      <Polygon points={`${hx + hw},${hy + hh + 14} ${hx + hw - 7},${hy + hh + 10} ${hx + hw - 7},${hy + hh + 18}`} fill={DT.varColor} />
      <SvgText x={hx + hw / 2} y={hy + hh + 28} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">W (ft)</SvgText>

      {/* Sash height dimension */}
      <Line x1={hx + hw + 16} y1={sashY} x2={hx + hw + 16} y2={hy + hh - 8} stroke={DT.varColor} strokeWidth="1.2" />
      <Polygon points={`${hx + hw + 16},${sashY} ${hx + hw + 12},${sashY + 7} ${hx + hw + 20},${sashY + 7}`} fill={DT.varColor} />
      <Polygon points={`${hx + hw + 16},${hy + hh - 8} ${hx + hw + 12},${hy + hh - 15} ${hx + hw + 20},${hy + hh - 15}`} fill={DT.varColor} />
      <SvgText x={hx + hw + 26} y={(sashY + hy + hh - 8) / 2} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} fontWeight="700"
        rotation="-90" originX={hx + hw + 26} originY={(sashY + hy + hh - 8) / 2}>H_sash</SvgText>

      {/* A badge */}
      <Rect x={hx + hw / 2 - 30} y={(sashY + hy + hh) / 2 - 12} width={60} height={24} fill="rgba(0,0,0,0.55)" stroke={DT.varColor} strokeWidth="1" />
      <SvgText x={hx + hw / 2} y={(sashY + hy + hh) / 2 + 3} fill={DT.varColor} fontSize="11" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">A_face</SvgText>

      {/* Labels */}
      <LeaderLine x1={hx + hw / 2} y1={hy + hh + 28} x2={hx + hw / 2} y2={235} varName="W" desc="sash width" anchor="middle" />
      <LeaderLine x1={hx + hw + 26} y1={(sashY + hy + hh) / 2} x2={335} y2={215} varName="H_sash" desc="opening height" anchor="end" />
      <LeaderLine x1={hx + hw / 2} y1={(sashY + hy + hh) / 2 + 12} x2={25} y2={215} varName="A_face" desc="ft²" anchor="start" />

      <FormulaTag formula="A_face = W · H_sash   (ft²)" />
    </DiagramCanvas>
  );
}
